// ---------------------------------------------------------------------------
// useProgressOverviewV3 — hook de carga + polling do endpoint consolidado.
// ---------------------------------------------------------------------------
// Consome ``GET /api/v3/progress_overview/`` (``InputsV3ProgressOverviewView``)
// e alimenta cockpit + panes do ``ProgressDrawerV3`` + o badge de status
// do mes na view principal.
//
// Polling adaptativo (PA-009 \u2014 calibrado mai/2026):
//   - cadencia ``rapida`` (4s) enquanto houver atividade viva no mes
//     (queue.running > 0, queued > 0, ou month_status em
//     ``processing``/``waiting``/``stale``/``action_needed``/
//     ``ready_to_calculate``); alinhada com a cadencia r\u00e1pida do
//     ``useBlocksV3`` para que cockpit/badge n\u00e3o atrase em rela\u00e7\u00e3o aos
//     blocos.
//   - cadencia ``lenta`` (30s) quando totalmente est\u00e1vel.
//   - cada poll envia ``If-None-Match`` com o ETag previo. Quando o
//     backend devolve 304 (nada mudou), preservamos os dados antigos
//     e atualizamos so o ETag (custo trivial).
//
// Custo: o 304 e' resolvido no servidor com 5 ``MAX(updated_at)``
// agregados \u2014 barato comparado ao caminho completo de queries
// pesadas. Antes deste fix, ambos os intervalos estavam em 60s
// (auditoria mai/2026 \u2014 C-3): consultor reportou que o cockpit
// "atrasava" enquanto os blocos j\u00e1 mostravam mudan\u00e7a, porque o
// poll do progress_overview era 15x mais lento que o do BlocksPane.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";

import { fetchProgressOverviewV3 } from "../../../../services/inputsV3Service";

const POLL_FAST_MS = 4_000;
const POLL_SLOW_MS = 30_000;

function isAlive(data) {
  if (!data) return false;
  const queue = data.queue || {};
  if ((queue.running || 0) > 0) return true;
  if ((queue.queued || 0) > 0) return true;
  const ms = data.month_status;
  // ``processing``/``waiting`` \u2014 status \u00f3bvios.
  // ``stale``/``ready_to_calculate``/``action_needed`` \u2014 sinais que o
  // pr\u00f3ximo tick do scheduler vai disparar trabalho; pollar r\u00e1pido para
  // n\u00e3o atrasar a transi\u00e7\u00e3o "stale \u2192 processing \u2192 ready".
  if (
    ms === "processing" ||
    ms === "waiting" ||
    ms === "stale" ||
    ms === "ready_to_calculate" ||
    ms === "action_needed"
  ) {
    return true;
  }
  return false;
}

export function useProgressOverviewV3({ year, month, enabled = true }) {
  const canFetch = !!(enabled && year && month);

  const [state, setState] = useState({
    loading: false,
    data: null,
    error: null,
  });
  const etagRef = useRef(null);
  const dataRef = useRef(null);

  useEffect(() => {
    dataRef.current = state.data;
  }, [state.data]);

  // (1) Primeira carga / mudanca de periodo: stale-while-revalidate.
  // Mantemos os dados anteriores ate o novo response chegar (evita o
  // "lag" do badge do mes regredindo para waiting).
  useEffect(() => {
    if (!canFetch) {
      setState({ loading: false, data: null, error: null });
      etagRef.current = null;
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    // Reseta ETag ao mudar periodo: o ETag e' por (impl, year, month).
    etagRef.current = null;
    fetchProgressOverviewV3({ year, month })
      .then(({ data, etag }) => {
        if (cancelled) return;
        etagRef.current = etag;
        setState({ loading: false, data: data || null, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        setState((prev) => ({ ...prev, loading: false, error: err }));
      });
    return () => {
      cancelled = true;
    };
  }, [canFetch, year, month]);

  // (2) Polling adaptativo. ``setTimeout`` recursivo (em vez de
  // ``setInterval``) pra recalcular o intervalo a cada tick com base
  // no estado mais recente.
  useEffect(() => {
    if (!canFetch) return undefined;
    let cancelled = false;
    let timer = null;

    const schedule = () => {
      if (cancelled) return;
      const interval = isAlive(dataRef.current) ? POLL_FAST_MS : POLL_SLOW_MS;
      timer = setTimeout(tick, interval);
    };

    const tick = () => {
      if (cancelled) return;
      fetchProgressOverviewV3({ year, month, etag: etagRef.current })
        .then(({ data, etag, notModified }) => {
          if (cancelled) return;
          if (etag) etagRef.current = etag;
          if (!notModified && data) {
            setState((prev) => ({ ...prev, data, error: null }));
          }
        })
        .catch(() => {
          // erro silencioso — preserva dado anterior.
        })
        .finally(() => {
          schedule();
        });
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [canFetch, year, month]);

  return state;
}

export default useProgressOverviewV3;
